import { useEffect, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query } from 'firebase/firestore';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../firebase/config';
import { listUserProfilesByName } from '../../database/userProfiles';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import { cn } from '../../lib/utils';
import PageHeader from './shell/PageHeader';
import { DEFAULT_MODE, MODES, modeLabel } from '../../constants/modes';

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const PLACE_LABELS = { 1: '1st', 2: '2nd', 3: '3rd' };
const placeLabel = (p) => PLACE_LABELS[p] || `${p}th`;
const placeAccent = {
  1: 'text-gold',
  2: 'text-silver',
  3: 'text-bronze',
};

const TournamentWinnerManagement = () => {
  const [users, setUsers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    userId: '',
    tournamentId: '',
    place: 1,
    mode: DEFAULT_MODE,
    prizeMoney: '',
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [users, tournamentsSnap, winnersSnap] = await Promise.all([
        listUserProfilesByName(),
        getDocs(query(collection(db, 'tournaments'), orderBy('name'))),
        getDocs(collection(db, 'tournamentWinners')),
      ]);
      setUsers(users);
      setTournaments(tournamentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setWinners(winnersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load data.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const resetForm = () =>
    setForm({ userId: '', tournamentId: '', place: 1, mode: DEFAULT_MODE, prizeMoney: '' });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.userId || !form.tournamentId) {
      toast.error('Pick a user and a tournament.');
      return;
    }
    setSaving(true);
    try {
      const winnerData = {
        userId: form.userId,
        userName: users.find((u) => u.id === form.userId)?.username || 'Unknown',
        tournamentId: form.tournamentId,
        tournamentName: tournaments.find((t) => t.id === form.tournamentId)?.name || 'Unknown',
        place: Number(form.place),
        mode: form.mode,
        prizeMoney: form.prizeMoney ? parseFloat(form.prizeMoney) : 0,
        awardedAt: new Date(),
      };
      await addDoc(collection(db, 'tournamentWinners'), winnerData);
      toast.success('Winner recorded.');
      resetForm();
      await fetchAll();
    } catch (err) {
      toast.error('Could not add winner.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'tournamentWinners', deleteTarget.id));
      toast.success('Winner removed.');
      setWinners((w) => w.filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Tournaments"
        title="Tournament winners"
        description="Record 1st / 2nd / 3rd place finishers per tournament and mode. Prize money is optional and shown on player profiles."
      />

      <Card className="border-border/60 bg-background/40">
        <CardHeader>
          <CardTitle className="font-display text-lg">Record a result</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="grid gap-5 md:grid-cols-2">
            <Field label="Player" required>
              <NativeSelect
                value={form.userId}
                onChange={(e) => setForm({ ...form, userId: e.target.value })}
              >
                <option value="">— Select player —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username || u.id}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Tournament" required>
              <NativeSelect
                value={form.tournamentId}
                onChange={(e) => setForm({ ...form, tournamentId: e.target.value })}
              >
                <option value="">— Select tournament —</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Place">
              <NativeSelect
                value={form.place}
                onChange={(e) => setForm({ ...form, place: Number(e.target.value) })}
              >
                <option value={1}>1st</option>
                <option value={2}>2nd</option>
                <option value={3}>3rd</option>
              </NativeSelect>
            </Field>
            <Field label="Mode">
              <NativeSelect
                value={form.mode}
                onChange={(e) => setForm({ ...form, mode: e.target.value })}
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    {modeLabel(m)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Prize money" hint="USD, optional">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.prizeMoney}
                onChange={(e) => setForm({ ...form, prizeMoney: e.target.value })}
                placeholder="0"
              />
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={saving} className="w-full md:w-auto">
                <Plus className="h-4 w-4" />
                {saving ? 'Saving…' : 'Record winner'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <section className="mt-12">
        <div className="mb-4 flex items-center gap-4">
          <span className="h-px w-6 bg-heat" />
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            History
          </h2>
        </div>

        {loading ? (
          <div className="divide-y divide-border/60 border-y border-border/60">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex h-14 animate-pulse items-center gap-4 px-2">
                <div className="h-3 w-10 bg-muted" />
                <div className="h-4 w-40 bg-muted" />
                <div className="ml-auto h-3 w-20 bg-muted" />
              </div>
            ))}
          </div>
        ) : winners.length === 0 ? (
          <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
            No winners recorded yet.
          </div>
        ) : (
          <ul className="divide-y divide-border/60 border-y border-border/60">
            {winners.map((w) => (
              <li
                key={w.id}
                className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-2 py-3 sm:grid-cols-[auto_1fr_auto_auto_auto_auto]"
              >
                <span
                  className={cn(
                    'font-mono text-[11px] font-semibold uppercase tracking-[0.2em] tabular-nums',
                    placeAccent[w.place] || 'text-muted-foreground'
                  )}
                >
                  {placeLabel(w.place)}
                </span>
                <div className="min-w-0">
                  <div className="truncate font-display text-base font-medium">{w.userName}</div>
                  <div className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {w.tournamentName}
                  </div>
                </div>
                <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:inline">
                  {w.mode}
                </span>
                <span className="hidden font-mono text-xs tabular-nums text-foreground/80 sm:inline">
                  ${w.prizeMoney || 0}
                </span>
                <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:inline">
                  {w.awardedAt?.toDate
                    ? w.awardedAt.toDate().toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: '2-digit',
                      })
                    : '—'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(w)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove winner?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Removes <span className="font-medium text-foreground">{deleteTarget?.userName}</span>'s{' '}
            {deleteTarget && placeLabel(deleteTarget.place)} finish in{' '}
            <span className="font-medium text-foreground">{deleteTarget?.tournamentName}</span>.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentWinnerManagement;
